import { defineStore } from 'pinia'
import * as Session from 'supertokens-web-js/recipe/session'
import { useToast } from '@/components/ui/toast/use-toast'
import { useLogger } from '~/composables/useLogger'
import { useAuth } from '~/composables/useAuth'
import type { Role } from '~/types/roles'

interface UserSettings {
  showHidden: boolean
  showArchived: boolean
  colorMode: 'light' | 'dark' | 'system'
  fontPreference: 'sans-serif' | 'serif' | 'monospace'
  language?: string
  autoRefresh: boolean
  useMemories: boolean
  showDuplicates: boolean
  showZeroScores: boolean
  searchNotes: boolean
  quickAdd: boolean
  city: string
  state: string
}

interface UserResponse {
  userId: string
  userName: string
  email: string
  role: Role
  avatar?: string
  settings: UserSettings
  phoneNumber?: string
}

interface UserStatsResponse {
  percentageReviewed: number
  totalBubbles: number
  bubblesPerType: Record<string, number>
  totalCollections: number
  totalChats: number
  storageUsed: number
  accountCreated: string
}

const defaultSettings: UserSettings = {
  showHidden: false,
  showArchived: false,
  colorMode: 'system',
  fontPreference: 'sans-serif',
  language: 'en',
  autoRefresh: true,
  useMemories: true,
  showDuplicates: false,
  showZeroScores: false,
  searchNotes: true,
  quickAdd: false,
  city: 'San Francisco',
  state: 'CA',
}

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null as UserResponse | null,
    role: null as Role | null,
    isAuthChecking: false,
    name: null as string | null,
    email: null as string | null,
    phoneNumber: null as string | null,
    isLoggedIn: false,
    isLoading: true,
    isDeleting: false,
    avatarUrl: null as string | null,
    profileFetched: false,
    initialDataLoaded: false,
    settings: defaultSettings,
    stats: null as UserStatsResponse | null,
    error: null as string | null,
  }),

  getters: {
    isAdmin: (state) => state.role === 'admin',
    isBeta: (state) => state.role === 'beta',
    isUpgraded: (state) => !!state.role && !['free', 'basic'].includes(state.role),
    // hasChatAccess: (state) => !!state.role && !['free', 'basic'].includes(state.role),
    hasChatAccess: (state) => !!state.role && !['free'].includes(state.role),
    hasInsightsAccess: (state) =>
      !!state.role && ['admin', 'beta', 'premium', 'pro'].includes(state.role),
    defaultAvatarUrl: (state) => `https://robohash.org/${state.name || 'default'}`,
    capitalizedRole: (state) =>
      state.role ? state.role.charAt(0).toUpperCase() + state.role.slice(1) : null,
  },

  actions: {
    setAuthChecking(value: boolean) {
      this.isAuthChecking = value
    },

    async checkLoginStatus() {
      const logger = useLogger()
      this.setAuthChecking(true)
      try {
        const exists = await Session.doesSessionExist()
        if (exists && !this.profileFetched) {
          await this.fetchUserProfile()
        }
        return exists
      } catch (err: unknown) {
        logger.logError('Check login status error:', err)
        return false
      } finally {
        this.setAuthChecking(false)
      }
    },

    async fetchUserProfile() {
      const { toast } = useToast()
      const logger = useLogger()
      const { apiCall } = useAuth()
      if (this.profileFetched) return

      const runtimeConfig = useRuntimeConfig()
      this.isLoading = true

      try {
        const response = await apiCall(`${runtimeConfig.public.apiBase}/user`)

        if (!response.ok) throw new Error('Failed to fetch profile')

        const userData: UserResponse = await response.json()
        const mergedSettings = { ...defaultSettings, ...(userData.settings || {}) }

        this.$patch({
          user: userData,
          role: userData.role || 'free',
          email: userData.email || null,
          name: userData.userName || null,
          avatarUrl: userData.avatar || null,
          settings: mergedSettings,
          phoneNumber: userData.phoneNumber || null,
          profileFetched: true,
          isLoggedIn: true,
          initialDataLoaded: true,
        })
      } catch (err: unknown) {
        logger.logError('Profile fetch error:', err)
        this.$reset()
        toast({
          title: 'Error',
          description: 'Failed to fetch user profile',
          variant: 'destructive',
        })
        throw err
      } finally {
        this.isLoading = false
      }
    },

    async updateUserSettings(settings: Partial<UserSettings>) {
      const { toast } = useToast()
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()

      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/user/settings`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(settings),
        })

        if (!response.ok) throw new Error('Failed to update settings')

        this.settings = { ...this.settings, ...settings }
        toast({
          title: 'Success',
          description: 'User settings updated successfully',
        })
        return true
      } catch (error) {
        logger.logError('Settings update error:', error)
        toast({
          title: 'Error',
          description: 'Failed to update user settings',
          variant: 'destructive',
        })
        return false
      }
    },

    async updateUserName(username: string) {
      const { toast } = useToast()
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()

      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/user/name`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userName: username }),
        })

        if (!response.ok) throw new Error('Failed to update username')

        this.name = username
        toast({
          title: 'Success',
          description: 'Username updated successfully',
        })
        return true
      } catch (error) {
        logger.logError('Username update error:', error)
        toast({
          title: 'Error',
          description: 'Failed to update user name',
          variant: 'destructive',
        })
        return false
      }
    },

    async uploadAvatar(file: File) {
      const { toast } = useToast()
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/user/avatar`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        })

        if (!response.ok) throw new Error('Failed to upload avatar')

        const data = await response.json()
        this.avatarUrl = data.avatar_url
        toast({
          title: 'Success',
          description: 'Avatar uploaded successfully',
        })
        return true
      } catch (error) {
        logger.logError('Avatar upload error:', error)
        toast({
          title: 'Error',
          description: 'Failed to upload avatar',
          variant: 'destructive',
        })
        return false
      }
    },

    async fetchUserStats() {
      const { toast } = useToast()
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()

      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/user/stats`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) throw new Error('Failed to fetch stats')

        this.stats = await response.json()
        return this.stats
      } catch (error) {
        logger.logError('Stats fetch error:', error)
        toast({
          title: 'Error',
          description: 'Failed to fetch user stats',
          variant: 'destructive',
        })
        return null
      }
    },

    async deleteAccount() {
      const { toast } = useToast()
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()
      this.isDeleting = true
      this.error = null

      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/user/delete`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ confirm: true }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to delete account')
        }

        toast({
          title: 'Success',
          description: 'Account deleted successfully.',
        })
        return true
      } catch (err: unknown) {
        logger.logError('Account deletion error:', err)
        this.error =
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred during account deletion.'
        toast({
          title: 'Error',
          description: this.error,
          variant: 'destructive',
        })
        return false
      } finally {
        this.isDeleting = false
      }
    },

    async logout() {
      const { toast } = useToast()
      const logger = useLogger()
      try {
        await Session.signOut()
        this.$reset()
        window.location.href = '/auth'
      } catch (err) {
        logger.logError('Logout error:', err)
        toast({
          title: 'Error',
          description: 'Failed to logout',
          variant: 'destructive',
        })
      }
    },

    async updateUserPhoneNumber(phoneNumber: string) {
      const { toast } = useToast()
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()

      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/user/phone`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phone_number: phoneNumber }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || 'Failed to update phone number')
        }

        this.phoneNumber = phoneNumber
        toast({
          title: 'Success',
          description: 'Phone number updated successfully',
        })
        return true
      } catch (error) {
        logger.logError('Phone number update error:', error)
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update phone number',
          variant: 'destructive',
        })
        return false
      }
    },

    async deleteUserPhoneNumber() {
      const { toast } = useToast()
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()

      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/user/phone`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || 'Failed to remove phone number')
        }

        this.phoneNumber = null
        toast({
          title: 'Success',
          description: 'Phone number removed successfully',
        })
        return true
      } catch (error) {
        logger.logError('Phone number removal error:', error)
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to remove phone number',
          variant: 'destructive',
        })
        return false
      }
    },

    $reset() {
      this.user = null
      this.role = null
      this.isAuthChecking = false
      this.name = null
      this.email = null
      this.phoneNumber = null
      this.isLoggedIn = false
      this.isLoading = true
      this.avatarUrl = null
      this.profileFetched = false
      this.initialDataLoaded = false
      this.settings = { ...defaultSettings }
      this.stats = null
      this.error = null
      this.isDeleting = false
    },
  },
})
